/**
 * 国际跳棋棋盘组件
 * 10×10棋盘，严格按照国际比赛规则显示
 */

import React, { useState, useEffect } from 'react';
import { CheckersGameState, Position, CheckersPiece } from '../types/game';
import { cn } from '../lib/utils';

interface CheckersBoardProps {
  gameState: CheckersGameState;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  isZoomed?: boolean;
  gameMode?: 'pvp' | 'pvc';
}

const CheckersBoard: React.FC<CheckersBoardProps> = ({
  gameState,
  onCellClick,
  disabled = false,
  isZoomed = false,
  gameMode = 'pvp'
}) => {
  const board = gameState.board;

  // 棋盘尺寸配置 - 优化版本
  const getBoardConfig = () => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 667;
    const isLandscape = screenWidth > screenHeight;

    // 电脑端优化：根据屏幕高度动态调整
    const getDesktopSize = () => {
      const maxHeight = Math.min(screenHeight - 250, 900);
      const maxWidth = Math.min(screenWidth - 100, 900);
      const useHeight = maxHeight / screenWidth < maxWidth / screenHeight;

      if (useHeight) {
        const size = Math.min(maxHeight, 900);
        return {
          size,
          cellSize: size / 10,
          padding: size * 0.015
        };
      } else {
        const size = Math.min(maxWidth, 900);
        return {
          size,
          cellSize: size / 10,
          padding: size * 0.015
        };
      }
    };

    // 移动端优化：更好的适配
    const getMobileSize = () => {
      if (isLandscape) {
        // 横屏模式：充分利用宽度
        const availableWidth = Math.min(screenWidth - 80, 700);
        return {
          size: availableWidth,
          cellSize: availableWidth / 10,
          padding: 4
        };
      } else {
        // 竖屏模式：考虑手机安全区域
        const safeAreaTop = typeof window !== 'undefined' && window?.safeAreaInsets?.top || 0;
        const availableHeight = screenHeight - safeAreaTop - 300;
        const availableWidth = screenWidth - 40;
        const size = Math.min(availableHeight, availableWidth, 500);
        return {
          size,
          cellSize: size / 10,
          padding: Math.max(2, size * 0.008)
        };
      }
    };

    // 根据屏幕尺寸和缩放模式返回配置
    if (screenWidth >= 1024) {
      // 桌面端
      const baseSize = isZoomed ? getDesktopSize() : {
        size: Math.min(screenHeight - 300, 700),
        cellSize: Math.min((screenHeight - 300) / 10, 70),
        padding: 8
      };
      return baseSize;
    } else if (screenWidth >= 768) {
      // 平板端
      return getMobileSize();
    } else {
      // 移动端
      return getMobileSize();
    }
  };

  const boardConfig = getBoardConfig();

  // 简化的棋子样式函数 - 基于配置动态生成
  const getCellSize = (): string => {
    // 基于配置返回动态样式
    const cellSize = boardConfig.cellSize;
    return `w-full h-full`;
  };

  // 简化的棋子样式函数 - 基于配置动态生成
  const getPieceStyle = (piece: CheckersPiece): string => {
    const baseStyle = 'absolute inset-1 rounded-full shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 border-3 touch-manipulation';

    if (piece.player === 'black') {
      if (piece.type === 'king') {
        return `${baseStyle} bg-gradient-to-br from-gray-800 to-black border-yellow-400 shadow-2xl`;
      }
      return `${baseStyle} bg-gradient-to-br from-gray-700 to-gray-900 border-gray-600 hover:from-gray-600 hover:to-gray-800`;
    } else {
      if (piece.type === 'king') {
        return `${baseStyle} bg-gradient-to-br from-white to-gray-200 border-yellow-500 shadow-2xl`;
      }
      return `${baseStyle} bg-gradient-to-br from-white to-gray-100 border-gray-300 hover:from-gray-50 hover:to-gray-200`;
    }
  };

  const getCellStyle = (row: number, col: number): string => {
    const baseStyle = getCellSize();
    const isDark = (row + col) % 2 === 1; // 深色格子用于下棋

    if (isDark) {
      let style = `${baseStyle} bg-amber-700 hover:bg-amber-600 cursor-pointer relative touch-manipulation transition-colors duration-200`;

      // 高亮选中的棋子位置
      if (isSelected(row, col)) {
        style += ' ring-4 ring-blue-400 ring-opacity-80 bg-amber-500 shadow-inner animate-pulse';
      }

      // 高亮可能的移动位置
      if (isPossibleMove(row, col)) {
        const isCapture = isCaptureMove(row, col);
        if (isCapture) {
          style += ' ring-2 ring-red-400 ring-opacity-70 bg-red-500 bg-opacity-30 animate-pulse';
        } else {
          style += ' ring-2 ring-green-400 ring-opacity-70 bg-green-500 bg-opacity-30';
        }
      }

      return style;
    } else {
      return `${baseStyle} bg-amber-100 cursor-default`;
    }
  };

  const isSelected = (row: number, col: number): boolean => {
    return gameState.selectedPiece?.row === row && gameState.selectedPiece?.col === col;
  };

  const isPossibleMove = (row: number, col: number): boolean => {
    return gameState.possibleMoves?.some(move => move.to.row === row && move.to.col === col) || false;
  };

  const isCaptureMove = (row: number, col: number): boolean => {
    const move = gameState.possibleMoves?.find(move => move.to.row === row && move.to.col === col);
    return move?.captures ? move.captures.length > 0 : false;
  };

  const renderPiece = (piece: CheckersPiece | null, row: number, col: number) => {
    if (!piece) return null;

    const isSelectedCell = isSelected(row, col);
    const isCurrentPlayerPiece = piece.player === gameState.currentPlayer;

    // 王棋标记 - 简化版
    const kingSize = Math.min(boardConfig.cellSize * 0.6, 30);
    const kingIcon = Math.min(boardConfig.cellSize * 0.3, 16);

    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className={cn(
            getPieceStyle(piece),
            {
              // 选中效果
              "ring-4 ring-blue-500 ring-opacity-90 shadow-2xl animate-pulse scale-110": isSelectedCell,
              // 当前玩家棋子有微妙的发光效果
              "ring-2 ring-opacity-40": isCurrentPlayerPiece && !isSelectedCell,
              "ring-blue-400 ring-opacity-30": isCurrentPlayerPiece && piece.player === 'black' && !isSelectedCell,
              "ring-green-400 ring-opacity-30": isCurrentPlayerPiece && piece.player === 'white' && !isSelectedCell,
              // 对手的棋子稍微暗淡
              "opacity-85": !isCurrentPlayerPiece
            }
          )}
          style={{
            // 动态设置内边距
            margin: `${Math.max(boardConfig.cellSize * 0.05, 2)}px`
          }}
        >
          {/* 王棋标记 - 简化美化版 */}
          {piece.type === 'king' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {/* 皇冠图标 */}
              <div
                className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-pulse"
                style={{
                  width: `${kingSize}px`,
                  height: `${kingSize}px`,
                }}
              >
                <span
                  className="font-black text-yellow-900 select-none"
                  style={{
                    fontSize: `${kingIcon}px`
                  }}
                >♔</span>
              </div>
            </div>
          )}

          {/* 棋子光泽效果 */}
          <div
            className="absolute top-2 left-2 bg-white rounded-full opacity-40"
            style={{
              width: `${Math.min(boardConfig.cellSize * 0.15, 8)}px`,
              height: `${Math.min(boardConfig.cellSize * 0.15, 8)}px`,
            }}
          ></div>

          {/* 选中棋子的额外指示器 */}
          {isSelectedCell && (
            <div className="absolute -inset-1 rounded-full border-2 border-blue-400 animate-ping"></div>
          )}
        </div>
      </div>
    );
  };

  const renderMoveIndicator = (row: number, col: number) => {
    if (!isPossibleMove(row, col)) return null;

    const isCapture = isCaptureMove(row, col);
    const indicatorSize = Math.max(boardConfig.cellSize * 0.3, 12);

    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <div
          className={cn(
            "rounded-full border-2 animate-pulse shadow-lg flex items-center justify-center",
            isCapture
              ? "bg-red-500 border-red-700 opacity-90 animate-bounce"
              : "bg-green-500 border-green-700 opacity-80"
          )}
          style={{
            width: `${indicatorSize}px`,
            height: `${indicatorSize}px`,
          }}
        >
          {/* 图标指示 */}
          <span
            className={cn(
              "text-white font-bold select-none",
              isCapture ? "text-white" : "text-white"
            )}
            style={{
              fontSize: `${indicatorSize * 0.5}px`
            }}
          >
            {isCapture ? '✕' : '✓'}
          </span>
        </div>

        {/* 外圈动画效果 */}
        <div
          className={cn(
            "absolute rounded-full border-2 animate-ping pointer-events-none",
            isCapture ? "border-red-400" : "border-green-400"
          )}
          style={{
            width: `${indicatorSize * 1.3}px`,
            height: `${indicatorSize * 1.3}px`,
          }}
        />
      </div>
    );
  };

  const renderCoordinates = () => {
    // 禁用坐标显示以避免页面布局问题
    return null;
  };

  // 获取最后一步的移动标记位置
  const getLastMovePosition = (): Position | null => {
    if (gameState.moves.length === 0) return null;
    const lastMove = gameState.moves[gameState.moves.length - 1];
    return lastMove.to;
  };

  const lastMovePos = getLastMovePosition();

  return (
    <div className="flex flex-col items-center w-full max-w-7xl mx-auto px-2 sm:px-4">
      {/* 棋盘 - 优化尺寸和触摸交互 */}
      <div
        className="relative shadow-2xl transition-all duration-300 rounded-xl mx-auto touch-manipulation"
        style={{
          width: `${boardConfig.size}px`,
          height: `${boardConfig.size}px`,
          maxWidth: '100%',
          background: 'linear-gradient(135deg, #D2691E, #8B4513)',
          padding: `${boardConfig.padding}px`
        }}
      >
        <div
          className="grid grid-cols-10 gap-0 rounded-lg overflow-hidden w-full h-full transition-all duration-300"
          style={{
            border: `${isZoomed ? 4 : 2}px solid #5D4037`
          }}
        >
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const isSelectedCell = isSelected(rowIndex, colIndex);
              const isLastMoveCell = lastMovePos?.row === rowIndex && lastMovePos?.col === colIndex;
              const isPossibleMoveCell = isPossibleMove(rowIndex, colIndex);

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={cn(
                    getCellStyle(rowIndex, colIndex),
                    {
                      "cursor-not-allowed opacity-75": disabled,
                      "z-20": isSelectedCell || isLastMoveCell,
                      "z-10": isPossibleMoveCell
                    }
                  )}
                  style={{
                    touchAction: 'manipulation', // 防止双击缩放
                  }}
                  onClick={() => {
                    if (disabled) return;
                    onCellClick({ row: rowIndex, col: colIndex });
                  }}
                  onTouchStart={(e) => {
                    // 防止默认的触摸行为
                    try {
                      e.preventDefault();
                    } catch (error) {
                      // 忽略错误
                    }
                  }}
                >
                {/* 棋子 */}
                {renderPiece(cell, rowIndex, colIndex)}

                {/* 移动指示器 */}
                {renderMoveIndicator(rowIndex, colIndex)}

                {/* 最后一步标记 - 简化版 */}
                {isLastMoveCell && !isSelectedCell && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-15">
                    <div
                      className="bg-yellow-400 rounded-full opacity-60 animate-pulse"
                      style={{
                        width: `${Math.max(boardConfig.cellSize * 0.2, 8)}px`,
                        height: `${Math.max(boardConfig.cellSize * 0.2, 8)}px`
                      }}
                    ></div>
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>

        {/* 坐标标记 */}
        {renderCoordinates()}
      </div>
    </div>
  );
};

export default CheckersBoard;